import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    HelpCircle,
    Search,
    Mail,
    FileText,
    Upload,
    Clock,
    Smartphone,
    Shield,
    BookOpen,
    ChevronRight,
} from "lucide-react"
import { Button, Card, CardBody, Input, Accordion, AccordionItem } from "@heroui/react"

const SUPPORT_EMAIL = "kayibue@gmail.com"

const quickLinks = [
    {
        icon: FileText,
        title: "Browse PASCOs",
        description: "Search and download past questions",
        href: "/",
    },
    {
        icon: Upload,
        title: "Upload PASCO",
        description: "Contribute past questions",
        href: "/upload",
    },
    {
        icon: Smartphone,
        title: "Link WhatsApp",
        description: "Connect your WhatsApp account",
        href: "/link-whatsapp",
    },
]

const faqs = [
    {
        category: "Account & Login",
        icon: Shield,
        questions: [
            {
                question: "How do I log in to PascoHub?",
                answer: "We use a secure OTP (One-Time Password) system. Simply enter your email address on the login page, and we'll send you a 6-digit code. Enter this code to access your account. No password needed!"
            },
            {
                question: "I didn't receive my OTP code. What should I do?",
                answer: "Check your spam/junk folder first. If you still don't see it, wait for the cooldown period to end and request a new code. Make sure your email address is entered correctly."
            },
            {
                question: "How do I link my WhatsApp to PascoHub?",
                answer: "There are several ways to link your WhatsApp: 1) Go to the dedicated Link WhatsApp page from the menu, 2) Visit your Profile page and use the WhatsApp Connection section, or 3) Click the WhatsApp button in the chat interface. You'll receive a unique linking code to send to our Pepper Bot on WhatsApp."
            },
        ]
    },
    {
        category: "PASCOs & Documents",
        icon: BookOpen,
        questions: [
            {
                question: "What file formats are supported for PASCO uploads?",
                answer: "PascoHub supports PDF files for PASCO uploads. Make sure your files are clear and readable for the best experience."
            },
            {
                question: "How long does PASCO processing take?",
                answer: "Processing time varies based on file size and complexity. Typically, it takes between 5-30 minutes. You'll see the status update on the Tasks page, and you can view the document once processing is complete."
            },
            {
                question: "Can I upload past questions anonymously?",
                answer: "Yes! When uploading, you can choose to upload as a guest without linking it to your account. Your contributions help fellow students!"
            },
            {
                question: "How does AI metadata extraction work?",
                answer: "Our AI automatically analyzes uploaded documents to extract course codes, course names, academic year, semester, and other metadata. You can also manually edit this information if needed."
            },
        ]
    },
    {
        category: "Tasks & Processing",
        icon: Clock,
        questions: [
            {
                question: "What are tasks in PascoHub?",
                answer: "Tasks represent background processing jobs like document analysis, metadata extraction, and file conversion. You can monitor their progress on the Tasks page."
            },
            {
                question: "Can I reschedule a failed task?",
                answer: "Yes! Navigate to the Tasks page, find the failed task, and click 'Reschedule'. The task will be queued for processing again."
            },
            {
                question: "Why did my task fail?",
                answer: "Tasks can fail due to various reasons: corrupted files, unsupported content, or temporary server issues. Check the error message for details, and try rescheduling or re-uploading the document."
            },
        ]
    },
]

export default function HelpCenterPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")

    const handleContactSupport = () => {
        const subject = encodeURIComponent("PascoHub Support Request")
        const body = encodeURIComponent("Hi,\n\nI need help with:\n\n[Please describe your issue here]\n\nThank you!")
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
    }

    // Filter FAQs based on search
    const filteredFaqs = faqs.map(category => ({
        ...category,
        questions: searchQuery === ""
            ? category.questions
            : category.questions.filter(q =>
                q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                q.answer.toLowerCase().includes(searchQuery.toLowerCase())
            )
    })).filter(category => category.questions.length > 0)

    return (
        <div className="h-full overflow-auto bg-background">
            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="max-w-3xl mx-auto space-y-6">
                    {/* Hero Section */}
                    <div className="text-center space-y-3 py-4">
                        <div className="flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded bg-primary/10">
                                <HelpCircle className="h-6 w-6 text-primary" />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground">How can we help?</h1>
                        <p className="text-sm text-default-500">
                            Search our FAQs or browse topics below
                        </p>
                        <div className="max-w-md mx-auto">
                            <Input
                                type="text"
                                placeholder="Search for help..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="md"
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                classNames={{
                                    inputWrapper: "rounded bg-background border border-default-200 hover:border-default-300",
                                }}
                            />
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="grid grid-cols-3 gap-3">
                        {quickLinks.map((link, index) => (
                            <Card
                                key={index}
                                isPressable
                                onPress={() => navigate(link.href)}
                                className="border border-default-200 rounded shadow-none hover:border-primary/50 hover:bg-primary/5 transition-all"
                            >
                                <CardBody className="p-3 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                                            <link.icon className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-foreground">{link.title}</p>
                                            <p className="text-[10px] text-default-400 hidden sm:block">{link.description}</p>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>

                    {/* FAQs by Category */}
                    <div className="space-y-4">
                        <h3 className="text-base font-semibold text-foreground">Frequently Asked Questions</h3>

                        {filteredFaqs.length === 0 ? (
                            <Card className="border border-default-200 rounded shadow-none">
                                <CardBody className="py-8 text-center">
                                    <p className="text-sm text-default-500">No results found for "{searchQuery}"</p>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        className="mt-2 rounded"
                                        onPress={() => setSearchQuery("")}
                                    >
                                        Clear search
                                    </Button>
                                </CardBody>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {filteredFaqs.map((category, catIndex) => (
                                    <Card key={catIndex} className="border border-default-200 rounded shadow-none overflow-hidden">
                                        <div className="flex items-center gap-2 px-4 py-3 bg-default-50 border-b border-default-100">
                                            <category.icon className="h-4 w-4 text-primary" />
                                            <span className="text-sm font-medium text-foreground">{category.category}</span>
                                        </div>
                                        <CardBody className="p-0">
                                            <Accordion
                                                variant="light"
                                                className="px-0"
                                                itemClasses={{
                                                    base: "px-4",
                                                    title: "text-sm font-normal text-foreground",
                                                    trigger: "py-3 data-[hover=true]:bg-default-50",
                                                    content: "text-sm text-default-600 pb-4 pt-0",
                                                    indicator: "text-default-400",
                                                }}
                                            >
                                                {category.questions.map((faq, faqIndex) => (
                                                    <AccordionItem
                                                        key={faqIndex}
                                                        aria-label={faq.question}
                                                        title={faq.question}
                                                    >
                                                        {faq.answer}
                                                    </AccordionItem>
                                                ))}
                                            </Accordion>
                                        </CardBody>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Contact Support */}
                    <Card className="border border-default-200 rounded shadow-none bg-gradient-to-r from-primary/5 to-primary/10">
                        <CardBody className="p-5">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="text-center sm:text-left">
                                    <h3 className="font-semibold text-foreground">
                                        Still need help?
                                    </h3>
                                    <p className="text-sm text-default-500 mt-0.5">
                                        We're here to assist you
                                    </p>
                                </div>
                                <Button
                                    color="primary"
                                    onPress={handleContactSupport}
                                    className="rounded"
                                    startContent={<Mail className="h-4 w-4" />}
                                    endContent={<ChevronRight className="h-4 w-4" />}
                                >
                                    Contact Support
                                </Button>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Footer note */}
                    <p className="text-center text-xs text-default-400 pb-4">
                        PascoHub — Helping students access past questions easily
                    </p>
                </div>
            </main>
        </div>
    )
}
